/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useRef } from 'preact/hooks';
import { BaseLegend } from './BaseLegend';
import { useEvents } from './useEvents';
import { LegendProps } from './Legend.type';
import { getLegendNavUtils } from './LegendNavUtils';
import { getAriaProps, getDatatipText, getGridNums } from './LegendUtils';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { useLegendTooltip } from './useLegendTooltip';
import { useItemFocus } from './useItemFocus';
import { useLegendLayout } from './useLegendLayout';
import { styles } from './themes/LegendStyles.css';

/**
 * A legend displays an interactive description of symbols, colors, etc., used in graphical information representations.
 */
export function Legend<K extends string | number>({
  orientation = 'horizontal',
  items,
  isReadOnly = true,
  onItemAction,
  onInput,
  ...props
}: LegendProps<K>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const rootDimsRef = useRef<DOMRect>();

  const { getItem, getDetailFromInfo, getPrevItemInfo, getNextItemInfo } = getLegendNavUtils(items);

  const { layoutInfo, isColumnWidthValidRef } = useLegendLayout({
    rootRef,
    rootDimsRef,
    items,
    orientation
  });

  const { hoveredItemInfo, focusedItemInfo, ...eventsProps } = useEvents<K>(
    !isReadOnly,
    props.hiddenIds != null,
    getDetailFromInfo,
    getPrevItemInfo,
    getNextItemInfo,
    onItemAction,
    onInput
  );

  const { focusedItemRef } = useItemFocus(focusedItemInfo);
  const datatipText = getDatatipText(items, getItem, focusedItemInfo, hoveredItemInfo);
  const { tooltipContent, tooltipProps } = useLegendTooltip({
    text: datatipText,
    rootDimsRef,
    focusedItemInfo,
    focusedItemRef
  });

  const ariaProps = getAriaProps(isReadOnly, props['aria-label'], props.hiddenIds);
  const mergedProps = mergeProps(eventsProps, tooltipProps);
  const { itemsPerCol, itemsPerRow } = getGridNums(items.length, layoutInfo[0]);
  const { legendStyle } = styles;

  return (
    <div
      ref={rootRef}
      tabIndex={isReadOnly ? undefined : 0}
      class={legendStyle}
      {...ariaProps}
      {...mergedProps}>
      <BaseLegend
        {...props}
        focusedItemRef={focusedItemRef}
        items={items}
        isReadOnly={isReadOnly}
        orientation={orientation}
        sectionIdx={0}
        itemsPerRow={itemsPerRow}
        isColumnWidthValidRef={isColumnWidthValidRef}
        itemsPerCol={itemsPerCol}
        itemWidth={layoutInfo[0]?.isWrapped ? layoutInfo[0]?.maxItemWidth : undefined}
        focusedItemInfo={focusedItemInfo}
        hoveredItemInfo={hoveredItemInfo}
        activeId={eventsProps['aria-activedescendant']}
      />
      {tooltipContent}
    </div>
  );
}
